import { MenuDivider, MenuItem, useDisclosure } from "@chakra-ui/react";
import ButtonMenu from "components/Menu/ButtonMenu";
import { TertiaryButtonWithRef } from "components/Button";
import MenuItemIcon from "components/Icon/MenuItemIcon";
import { NewHireJourney } from "models/v2/newHireJourney";
import {
  FiUser, FiUsers, FiEdit, FiExternalLink, FiPlusSquare, FiLogOut, FiUserX,
} from "react-icons/fi";
import { IoMdPause, IoMdPlay } from "react-icons/io";
import UpdateOnboardingPlanButtonWithModal from "components/ModalForm/NewHireJourneyEditForm/NewHireJourneyEditButtonWithModal";
import AddPathsModal from "features/Path/Components/AddPathsToNewHireModal";
import { useCurrentUserQuery } from "features/User/hooks";
import { canCloseJourney } from "features/NewHireJourney/helpers";
import ShowSupportCrewButtonDrawer from "./ShowSupporCrewButtontDrawer";
import CloseJourneyModal from "./CloseJourneyModal";
import { useUpdateNewHireJourneyStatus } from "../hooks";

interface NewHireJourneyOptionMenuProps {
  newHireJourney: NewHireJourney;
  size?: string;
  onOpenProfile?: () => void;
  canEdit?: boolean;
}

export default function NewHireJourneyOptionMenu({
  newHireJourney,
  size = "xs",
  onOpenProfile,
  canEdit = false,
}: NewHireJourneyOptionMenuProps) {
  const { toggle } = useUpdateNewHireJourneyStatus(newHireJourney.id);
  const actionsEnabled = newHireJourney.actionsStatus === "enabled";
  const { data: currentUser } = useCurrentUserQuery();
  const closeModal = useDisclosure();
  const closeAndDeactivateModal = useDisclosure();
  const newHireIsActive = !!newHireJourney.newHire && !newHireJourney.newHire.deactivatedAt;

  const automationText = actionsEnabled ? "Pause All Automations" : "Resume All Automations";

  const canClose = canCloseJourney(newHireJourney, currentUser);

  return (
    <>
      <ButtonMenu
        buttonComponent={TertiaryButtonWithRef}
        label="Manage"
        ariaLabel="Manage new hire journey"
        size={size}
        includeIcon
        inPortal
      >
        <MenuItem icon={<MenuItemIcon icon={FiUser} />} onClick={onOpenProfile}>
          View/Update Profile
        </MenuItem>

        <ShowSupportCrewButtonDrawer newHireJourney={newHireJourney}>
          <MenuItem icon={<MenuItemIcon icon={FiUsers} />}>Support Crew</MenuItem>
        </ShowSupportCrewButtonDrawer>

        {newHireJourney.onboardingPlanUrl && (
          <MenuItem
            as="a"
            href={newHireJourney.onboardingPlanUrl}
            target="_blank"
            rel="noopener noreferrer"
            icon={<MenuItemIcon icon={FiExternalLink} />}
          >
            View Onboarding Plan
          </MenuItem>
        )}

        {/* Onboarding plan is editable by anyone involved in the journey — not gated by canEdit. */}
        <UpdateOnboardingPlanButtonWithModal newHireJourney={newHireJourney}>
          <MenuItem icon={<MenuItemIcon icon={FiEdit} />}>
            {newHireJourney.onboardingPlanUrl ? "Update Onboarding Plan" : "Add Onboarding Plan"}
          </MenuItem>
        </UpdateOnboardingPlanButtonWithModal>

        {canEdit && (
          <>
            <AddPathsModal newHireJourneyId={newHireJourney?.id}>
              <MenuItem icon={<MenuItemIcon icon={FiPlusSquare} />}>Add Paths</MenuItem>
            </AddPathsModal>

            {!newHireJourney.isPending && (
              <MenuItem
                icon={<MenuItemIcon icon={actionsEnabled ? IoMdPause : IoMdPlay} />}
                onClick={() => toggle(newHireJourney.actionsStatus)}
              >
                {automationText}
              </MenuItem>
            )}
          </>
        )}

        {canClose && (
          <>
            <MenuDivider />
            <MenuItem
              icon={<MenuItemIcon icon={FiLogOut} />}
              onClick={closeModal.onOpen}
            >
              End Journey Early
            </MenuItem>
            {newHireIsActive && (
              <MenuItem
                icon={<MenuItemIcon icon={FiUserX} />}
                color="red.600"
                onClick={closeAndDeactivateModal.onOpen}
              >
                End Journey & Deactivate User
              </MenuItem>
            )}
          </>
        )}
      </ButtonMenu>

      {canClose && (
        <>
          <CloseJourneyModal
            isOpen={closeModal.isOpen}
            onClose={closeModal.onClose}
            journeyId={newHireJourney.id}
            newHireName={newHireJourney.newHire?.fullName ?? "this new hire"}
          />
          {newHireIsActive && newHireJourney.newHire && (
            <CloseJourneyModal
              isOpen={closeAndDeactivateModal.isOpen}
              onClose={closeAndDeactivateModal.onClose}
              journeyId={newHireJourney.id}
              newHireName={newHireJourney.newHire.fullName}
              withUserDeactivation
              newHire={newHireJourney.newHire}
            />
          )}
        </>
      )}
    </>
  );
}
